/** User-facing print failures. Raw transport errors remain diagnostic data. */
export const LABEL_PRINT_ERRORS = {
    paper:'打印机缺纸',cover:'打印机盖未关',disconnected:'打印机未连接',connection:'打印机连接失败',load:'打印组件加载失败',timeout:'打印机连接超时',permission:'蓝牙权限未开启',authorization:'打印机授权已失效',
    authorizationRead:'打印机授权读取失败',unsupported:'浏览器不支持蓝牙打印',incompatible:'打印机不兼容',secure:'请使用 HTTPS 打印',
    interrupted:'打印中断，请点击打印重试',busy:'标签正在发送',locked:'任务正在其他页面处理',
    identity:'部分花盆无 SKU',identityChanged:'部分花盆的 SKU 已失效，请重新生成标签',
    mismatch:'标签信息不匹配，请重新打开',read:'标签读取失败',readNetwork:'网络异常，标签未读取',readServer:'服务器出错，标签未读取',changed:'标签已变化，请重新生成',
    render:'标签生成失败',renderNetwork:'网络异常，标签未生成',logo:'标签 Logo 加载失败',pdf:'PDF 保存失败',
    save:'打印任务未保存，请勿关闭页面',quota:'浏览器空间不足，打印任务未保存',storagePermission:'浏览器不允许保存打印任务',
    queueRead:'待打印任务读取失败',remove:'打印任务取消失败',removeNetwork:'网络异常，任务未取消',removeTimeout:'取消超时，请重试',removeBusy:'任务正在其他设备处理',removeStale:'任务已更新，请重试',state:'标签状态未保存，请勿关闭页面',
    cloudOffline:'云打印机未连接',cloudTimeout:'云打印连接超时',cloudNetwork:'无法连接云打印服务',cloud:'云打印中断，请点击打印重试',
    login:'登录已过期，请重新登录',forbidden:'没有打印权限',
} as const;
const messages=new Set<string>(Object.values(LABEL_PRINT_ERRORS));
export const isLabelPrintMessage=(value:unknown):value is string=>typeof value==='string'&&messages.has(value);
export type LabelPrintStage='connect'|'read'|'render'|'send'|'save'|'pdf'|'queueRead'|'remove'|'state';
export class LabelPrintFailure extends Error {
    constructor(message:string,public original:unknown,public handled=false){super(message);this.name='LabelPrintFailure';}
}
export function labelPrintError(error:unknown,{stage='send',destination='bluetooth',uncertain=false}:{stage?:LabelPrintStage;destination?:'bluetooth'|'cloud'|'pdf';uncertain?:boolean}={}):string {
    if(error instanceof LabelPrintFailure)return error.message;
    const details=error as {name?:string;message?:string;reason?:string};
    const message=typeof details?.message==='string'?details.message:typeof error==='string'?error:'';
    if(isLabelPrintMessage(message))return message;
    if(details?.name==='ChunkLoadError'||/Loading chunk|dynamically imported module/i.test(message))return LABEL_PRINT_ERRORS.load;
    if(/登录|登录已变化/.test(message))return LABEL_PRINT_ERRORS.login;
    if(stage==='remove'){
        if(/其他设备|其他页面|正在发送/.test(message))return LABEL_PRINT_ERRORS.removeBusy;
        if(/进度已更新|版本|已变化/.test(message))return LABEL_PRINT_ERRORS.removeStale;
        if(/超时|timeout|timed out/i.test(message))return LABEL_PRINT_ERRORS.removeTimeout;
        if(/network|failed to fetch|fetch failed|网络|无法连接服务器|未同步|ECONN/i.test(message))return LABEL_PRINT_ERRORS.removeNetwork;
        if(/没有权限|无权/.test(message))return LABEL_PRINT_ERRORS.forbidden;
    }
    if(details?.name==='QuotaExceededError')return LABEL_PRINT_ERRORS.quota;
    if(stage==='save'||stage==='queueRead'||stage==='remove'||stage==='state'){
        if(details?.name==='SecurityError')return LABEL_PRINT_ERRORS.storagePermission;
        return LABEL_PRINT_ERRORS[stage];
    }
    if(/缺纸|out of paper|paper out/i.test(message))return LABEL_PRINT_ERRORS.paper;
    if(/盖未关|cover open/i.test(message))return LABEL_PRINT_ERRORS.cover;
    if(/核对出纸/.test(message))return LABEL_PRINT_ERRORS.interrupted;
    if(/^请先?连接打印机|^请连接打印机|打印机.*断开/.test(message))return LABEL_PRINT_ERRORS.disconnected;
    if(details?.reason==='interrupted')return LABEL_PRINT_ERRORS.interrupted;
    if(/授权.*失效/.test(message))return LABEL_PRINT_ERRORS.authorization;
    if(/读取授权|授权读取/.test(message))return LABEL_PRINT_ERRORS.authorizationRead;
    if(/不兼容|通道不可写/.test(message))return LABEL_PRINT_ERRORS.incompatible;
    if(/HTTPS|安全连接/.test(message))return LABEL_PRINT_ERRORS.secure;
    if(/不支持.*蓝牙|Bluefy|请用 Chrome/.test(message))return LABEL_PRINT_ERRORS.unsupported;
    if(/其他页面/.test(message))return LABEL_PRINT_ERRORS.locked;
    if(/正在发送/.test(message))return LABEL_PRINT_ERRORS.busy;
    if(details?.reason==='sample'||details?.reason==='disconnected')return /超时/.test(message)?LABEL_PRINT_ERRORS.timeout:LABEL_PRINT_ERRORS.connection;
    if(details?.reason==='permission'||details?.name==='NotAllowedError'||details?.name==='SecurityError')return LABEL_PRINT_ERRORS.permission;
    if(/没有权限|无权/.test(message))return LABEL_PRINT_ERRORS.forbidden;
    if(/已变化|数量不一致|不存在|已删除/.test(message))return LABEL_PRINT_ERRORS.changed;
    const network=/network|failed to fetch|fetch failed|网络|无法连接服务器|ECONN/i.test(message);
    if(stage==='read')return network?LABEL_PRINT_ERRORS.readNetwork:/服务器|服务暂/.test(message)?LABEL_PRINT_ERRORS.readServer:LABEL_PRINT_ERRORS.read;
    if(stage==='render')return network?LABEL_PRINT_ERRORS.renderNetwork:LABEL_PRINT_ERRORS.render;
    if(stage==='pdf'||destination==='pdf')return LABEL_PRINT_ERRORS.pdf;
    if(destination==='cloud'){
        if(/offline|离线|未连接|not connected/i.test(message))return LABEL_PRINT_ERRORS.cloudOffline;
        if(/timeout|timed out|超时/i.test(message))return LABEL_PRINT_ERRORS.cloudTimeout;
        if(network)return LABEL_PRINT_ERRORS.cloudNetwork;
        return LABEL_PRINT_ERRORS.cloud;
    }
    if(uncertain||stage==='send')return LABEL_PRINT_ERRORS.interrupted;
    return /超时/.test(message)?LABEL_PRINT_ERRORS.timeout:LABEL_PRINT_ERRORS.connection;
}
